import { styled } from "@mui/material/styles";
import { Tab, TabProps, Tabs, TabsProps } from "@mui/material";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useTheme } from "@/contexts/theme";
import { FONT_FAMILY } from "@/utils/font-family";
import { CustomTabPanel } from "@/components/tabs/tab-panel";
import { ITabsProps, type TAB_TYPE } from "@/components/tabs/interface";
import Stack from "@mui/material/Stack";
import { SecondaryButton } from "@/components/button/secondary-button";
import { useEffect, useState } from "react";
import { useStyle } from "./style";

const ButtonTabs = styled(Tabs)((
  props: TabsProps & { selectedColor: "primary" | "secondary" },
) => {
  return {
    "& .MuiTabs-indicator": {
      backgroundColor: "transparent",
    },
    "& .MuiButtonBase-root": {
      textTransform: "initial",
      padding: "10px",
    },
    "& .MuiTabs-flexContainer": {
      gap: "10px",
    },
  };
});

const ButtonTab = styled(Tab)((
  props: TabProps & { selectedColor: "primary" | "secondary" },
) => {
  const { primaryColor, secondColor } = useGomakeTheme();
  const { selectedColor } = props;
  return {
    minWidth: "auto",
    minHeight: "auto",
    backgroundColor: primaryColor(100),
    color: primaryColor(700),
    borderRadius: "4px",
    ...FONT_FAMILY.Lexend(500, 16),
    "&.Mui-selected": {
      backgroundColor:
        selectedColor === "primary" ? primaryColor(500) : secondColor(500),
      color: "#FFFFFF",
    },
  };
});

const UnderlinedTabs = styled(Tabs)<
  TabsProps & { selectedColor: "primary" | "secondary" }
>(() => ({
  // Hide MUI's JS-positioned indicator — it misaligns in RTL and on layout shifts (e.g. side menu).
  // The underline lives on each UnderlinedTab via ::after instead.
  "& .MuiTabs-indicator": {
    display: "none",
  },
  "& .MuiButtonBase-root": {
    textTransform: "initial",
  },
  // HP theme: gray track only under the tab labels (flex container width), blue indicator overlays it
  '[data-theme="hp-client"] &': {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 0,
    minHeight: "var(--theme-tabs-height, 48px)",
    borderBottom: "none",
    "& .MuiTabs-flexContainer": {
      position: "relative",
      "&::after": {
        content: '""',
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "var(--theme-tabs-indicator-height, 2px)",
        background: "var(--theme-tabs-track-bg, #E8E8E8)",
        zIndex: 1,
      },
    },
    "& .MuiButtonBase-root": {
      padding: "var(--theme-tabs-item-padding, 14px 16px)",
      minHeight: "var(--theme-tabs-height, 48px)",
    },
  },
}));

const UnderlinedTab = styled(Tab)((
  props: TabProps & { selectedColor: "primary" | "secondary" },
) => {
  const { primaryColor, secondColor, neutralColor } = useGomakeTheme();
  const { selectedColor } = props;
  const finalColor = selectedColor === "secondary" ? secondColor : primaryColor;
  return {
    minWidth: "auto",
    color: neutralColor(300),
    position: "relative",
    ...FONT_FAMILY.Lexend(500, 16),
    // CSS underline stays attached to the tab — works in LTR/RTL and when the side menu resizes.
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      height: "3px",
      borderRadius: "2px",
      background: `linear-gradient(90deg, ${finalColor(500)}, ${finalColor(
        300,
      )})`,
      boxShadow: `0px 2px 8px ${finalColor(300)}88`,
      transform: "scaleX(0)",
      transformOrigin: "center",
      transition: "transform 0.3s ease",
      pointerEvents: "none",
      zIndex: 2,
    },
    "&.Mui-selected::after": {
      transform: "scaleX(1)",
    },
    "&:hover": {
      color:
        selectedColor === "secondary" ? secondColor(500) : primaryColor(500),
      opacity: 1,
    },
    "&.Mui-selected": {
      ...FONT_FAMILY.Lexend(500, 16),
      color:
        selectedColor === "secondary" ? secondColor(500) : primaryColor(500),
    },
    '[data-theme="hp-client"] &': {
      fontFamily: "var(--theme-tabs-label-font-family)",
      fontStyle: "normal",
      fontWeight: "var(--theme-tabs-label-font-weight, 400)",
      fontSize: "var(--theme-tabs-label-font-size, 16px)",
      lineHeight: "var(--theme-tabs-label-line-height, 20px)",
      color: "var(--theme-tabs-label-color, #1A1A1A)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      "&::after": {
        height: "var(--theme-tabs-indicator-height, 2px)",
        borderRadius: "var(--theme-tabs-indicator-radius, 4px)",
        background: "var(--theme-tabs-indicator-bg, #024AD8)",
        boxShadow: "none",
      },
      "&:hover": {
        color: "var(--theme-tabs-label-selected-color, #024AD8)",
      },
      "&.Mui-selected": {
        color: "var(--theme-tabs-label-selected-color, #024AD8)",
      },
    },
  };
});

const PrimaryTabsComponent = ({
  tabs,
  children,
  navigationButtons,
  onSelectTab,
  selectedColor,
  variant,
  selectedTabIndex = 0,
  withBackGround = false,
  backGroundColor = "#ffffff",
  style,
  TabsContainerStyle,
}: ITabsProps) => {
  const { classes } = useStyle();
  const { currentTheme } = useTheme();
  const [value, setValue] = useState<number>(selectedTabIndex);

  // Default: UnderlinedTabs. Use ButtonTabs only when variant is explicitly "ButtonedTabs". HP theme always uses UnderlinedTabs.
  const isHpClient = currentTheme === "hp-client";
  const buttonedVariant: TAB_TYPE = "ButtonedTabs";
  const useUnderlined = isHpClient || variant !== buttonedVariant;
  const TabComponent = useUnderlined ? UnderlinedTab : ButtonTab;
  const TabsContainer = useUnderlined ? UnderlinedTabs : ButtonTabs;

  const handleChange = (event, newValue) => {
    setValue(newValue);

    if (!!onSelectTab) {
      onSelectTab(newValue);
    }
  };

  useEffect(() => {
    setValue(selectedTabIndex);
  }, [selectedTabIndex, setValue]);

  return (
    <>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        position={"sticky"}
        top={0}
        zIndex={9}
        marginBottom={2}
        bgcolor={withBackGround ? backGroundColor : undefined}
        style={style}
      >
        <TabsContainer
          value={value}
          onChange={handleChange}
          selectedColor={selectedColor}
          aria-label="tabs example"
          variant="scrollable"
          scrollButtons="auto"
          style={{ flex: 1, minWidth: 0, width: "100%", ...TabsContainerStyle }}
        >
          {tabs?.map((tab, index) => (
            <TabComponent
              key={`tab-${index}`}
              label={tab?.title}
              className={useUnderlined ? undefined : "gm-tab-btn"}
              selectedColor={selectedColor}
              disabled={tab?.disabled}
            />
          ))}
        </TabsContainer>
        {children && <div style={{ flexShrink: 0 }}>{children}</div>}
      </Stack>
      {tabs?.map((tab, index: number) =>
        tab?.component ? (
          <CustomTabPanel key={"tabs" + index} value={value} index={index}>
            {tab.component}
            <div style={classes.tabStyle}>
              {navigationButtons && index + 1 < tabs.length && (
                <SecondaryButton
                  variant={"contained"}
                  onClick={() => setValue(index + 1)}
                >
                  Next
                </SecondaryButton>
              )}
            </div>
          </CustomTabPanel>
        ) : null,
      )}
    </>
  );
};

export {
  ButtonTab,
  ButtonTabs,
  UnderlinedTab,
  UnderlinedTabs,
  PrimaryTabsComponent,
};
